// src/components/admin/teamTaskbox/TaskCalendarView.js
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Flag, Clock } from 'lucide-react';
import teamTasksService from '../../../services/teamTasks.service';
import taskService from '../../../services/task.service';
import { useAuth } from '../../../hooks/useAuth';

const TaskCalendarView = ({ tasks, onTaskClick }) => {
    const { user } = useAuth();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [viewMode, setViewMode] = useState('month'); // 'month', 'week'
    const [loading, setLoading] = useState(false);
    
    const priorityColors = {
        immediate: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-300',
        urgent: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-300',
        next_in_line: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-300',
        scheduled: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-300',
        deferred: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400 border-gray-300'
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        return { daysInMonth, startingDayOfWeek, year, month };
    };

    const getWeekDays = (date) => {
        const week = [];
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - date.getDay()); // Start from Sunday

        for (let i = 0; i < 7; i++) {
            const day = new Date(startOfWeek);
            day.setDate(startOfWeek.getDate() + i);
            week.push(day);
        }

        return week;
    };

    const getTasksForDate = (date) => {
        return tasks.filter(task => {
            if (!task.deadline) return false;
            const taskDate = new Date(task.deadline);
            return (
                taskDate.getDate() === date.getDate() &&
                taskDate.getMonth() === date.getMonth() &&
                taskDate.getFullYear() === date.getFullYear()
            );
        });
    };

    // Helper functions to check user permissions and assignment
    const isUserAssigned = (task) => {
        if (!user?.id) return false;
        return task.assigned_employees?.some(emp => emp.id === user.id) ||
               task.assigned_employee_ids?.includes(user.id) ||
               task.assigned_to === user.id;
    };

    
    const navigateMonth = (direction) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(currentDate.getMonth() + direction);
        setCurrentDate(newDate);
    };

    const navigateWeek = (direction) => {
        const newDate = new Date(currentDate);
        newDate.setDate(currentDate.getDate() + (direction * 7));
        setCurrentDate(newDate);
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    const isToday = (date) => {
        const today = new Date();
        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        );
    };

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const renderMonthView = () => {
        const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
        const days = [];

        // Empty cells for days before the month starts
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(
                <div key={`empty-${i}`} className="bg-gray-50 dark:bg-gray-900 p-2 min-h-[120px]">
                    <div className="text-gray-400 text-sm"></div>
                </div>
            );
        }

        // Days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dayTasks = getTasksForDate(date);
            const isTodayDate = isToday(date);

            days.push(
                <div
                    key={day}
                    className={`bg-white dark:bg-gray-800 p-2 min-h-[120px] border border-gray-200 dark:border-gray-700 ${
                        isTodayDate ? 'ring-2 ring-blue-500' : ''
                    }`}
                >
                    <div className={`text-sm font-medium mb-2 ${
                        isTodayDate
                            ? 'bg-blue-600 text-white w-7 h-7 rounded-full flex items-center justify-center'
                            : 'text-gray-700 dark:text-gray-300'
                    }`}>
                        {day}
                    </div>
                    <div className="space-y-1">
                        {dayTasks.slice(0, 3).map(task => (
                            <div
                                key={task.id}
                                className={`px-2 py-1 rounded text-xs cursor-pointer border-l-2 ${priorityColors[task.priority]} hover:opacity-80 transition-opacity group relative`}
                                title={task.title}
                            >
                                <div className="flex items-start justify-between gap-1">
                                    <div
                                        className="flex-1 min-w-0"
                                        onClick={() => onTaskClick(task)}
                                    >
                                        <div className="font-medium truncate">{task.title}</div>
                                        {task.estimated_hours && (
                                            <div className="text-xs opacity-75">{task.estimated_hours}h</div>
                                        )}
                                    </div>
                                                                    </div>
                            </div>
                        ))}
                        {dayTasks.length > 3 && (
                            <div className="text-xs text-gray-500 pl-2">
                                +{dayTasks.length - 3} more
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return days;
    };

    const renderWeekView = () => {
        const weekDays = getWeekDays(currentDate);

        return weekDays.map((date, index) => {
            const dayTasks = getTasksForDate(date);
            const isTodayDate = isToday(date);

            return (
                <div
                    key={index}
                    className={`flex-1 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 last:border-r-0 ${
                        isTodayDate ? 'ring-2 ring-blue-500 ring-inset' : ''
                    }`}
                >
                    <div className={`p-3 border-b border-gray-200 dark:border-gray-700 ${
                        isTodayDate ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}>
                        <div className="text-center">
                            <div className="text-xs text-gray-600 dark:text-gray-400">{dayNames[date.getDay()]}</div>
                            <div className={`text-lg font-semibold ${
                                isTodayDate
                                    ? 'text-blue-600 dark:text-blue-400'
                                    : 'text-gray-900 dark:text-white'
                            }`}>
                                {date.getDate()}
                            </div>
                        </div>
                    </div>
                    <div className="p-2 space-y-2 min-h-[500px]">
                        {dayTasks.map(task => {
                            const progress = teamTasksService.calculateProgress(task);
                            const isOverdue = task.overdue_days > 0;

                            return (
                                <div
                                    key={task.id}
                                    className={`p-2 rounded cursor-pointer border-l-4 ${priorityColors[task.priority]} hover:shadow-md transition-shadow group relative`}
                                >
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                        <div
                                            className="flex items-start gap-2 flex-1 min-w-0"
                                            onClick={() => onTaskClick(task)}
                                        >
                                            <Flag className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-sm line-clamp-2">{task.title}</div>
                                            </div>
                                        </div>
                                                                            </div>
                                    {isOverdue && (
                                        <div className="text-xs text-red-600 dark:text-red-400 mb-1">
                                            {task.overdue_days}d overdue
                                        </div>
                                    )}
                                    {task.estimated_hours && (
                                        <div className="flex items-center gap-1 text-xs opacity-75 mb-1">
                                            <Clock className="w-3 h-3" />
                                            {task.estimated_hours}h
                                        </div>
                                    )}
                                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1">
                                        <div
                                            className={`h-1 rounded-full ${
                                                progress === 100 ? 'bg-green-600' : 'bg-blue-600'
                                            }`}
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        });
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                        </h2>
                        <button
                            onClick={goToToday}
                            className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                        >
                            Today
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* View Mode Toggle */}
                        <div className="flex gap-1 mr-4">
                            <button
                                onClick={() => setViewMode('month')}
                                className={`px-3 py-1 text-sm rounded transition-colors ${
                                    viewMode === 'month'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                }`}
                            >
                                Month
                            </button>
                            <button
                                onClick={() => setViewMode('week')}
                                className={`px-3 py-1 text-sm rounded transition-colors ${
                                    viewMode === 'week'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                }`}
                            >
                                Week
                            </button>
                        </div>

                        {/* Navigation */}
                        <button
                            onClick={() => viewMode === 'month' ? navigateMonth(-1) : navigateWeek(-1)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        </button>
                        <button
                            onClick={() => viewMode === 'month' ? navigateMonth(1) : navigateWeek(1)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Calendar Grid */}
            {viewMode === 'month' ? (
                <>
                    {/* Day Names Header */}
                    <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700">
                        {dayNames.map(day => (
                            <div
                                key={day}
                                className="p-2 text-center text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900"
                            >
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Days Grid */}
                    <div className="grid grid-cols-7">
                        {renderMonthView()}
                    </div>
                </>
            ) : (
                /* Week View */
                <div className="flex border-t border-gray-200 dark:border-gray-700">
                    {renderWeekView()}
                </div>
            )}
        </div>
    );
};

export default TaskCalendarView;
